import { useContext, useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { AuthContext } from '../../../context/AuthContext';
import { useFeatureFlags } from '../../../context/FeatureFlagsContext';

import { useNavUi } from '../../../components/layout/navUiContext';

import { useWalkthrough } from '../walkthroughContext';

import { getTourForUser, getToursForUser } from '../tours/tourRegistry';

import {

  buildFirstRunSequence,

  consumeWalkthroughReplay,

  getDefaultAutoStartTourId,

  prepareStepTarget,

  queueWalkthroughReplay,

  replayWalkthrough,

  resolveAutoStartDashboardPath,

  resolveTourStartPath,

  shouldAutoStartWalkthrough,

  waitForLayoutPaint,

  WALKTHROUGH_REPLAY_KEY,

} from '../walkthroughOrchestrator';



function hasQueuedWalkthroughReplay() {

  if (typeof window === 'undefined') return false;

  try {

    return Boolean(window.sessionStorage.getItem(WALKTHROUGH_REPLAY_KEY));

  } catch {

    return false;

  }

}



/**

 * Coordinates first-run auto-start and queued replay after auth + layout are ready.

 */

export default function WalkthroughBootstrap() {

  const { user, isLoading } = useContext(AuthContext);

  const { isWalkthroughEnabled } = useFeatureFlags();

  const { persistedState, startTour, startSequence, isActive, endActiveTour } = useWalkthrough();

  const { openMobileNav, isMobile } = useNavUi();

  const navigate = useNavigate();

  const location = useLocation();

  const autoStartSessionRef = useRef(false);

  const replayHandledRef = useRef(false);



  useEffect(() => {

    if (!isWalkthroughEnabled || isLoading || !user) {

      return undefined;

    }



    if (isActive) {

      if (hasQueuedWalkthroughReplay()) {

        endActiveTour();

      }

      return undefined;

    }



    const replayTourId = consumeWalkthroughReplay();

    if (replayTourId) {

      replayHandledRef.current = true;

      const tour = getTourForUser(user, replayTourId);

      if (!tour) {

        return undefined;

      }



      const targetPath = resolveTourStartPath(user, tour);

      if (location.pathname !== targetPath) {

        queueWalkthroughReplay(replayTourId);

        navigate(targetPath, { replace: false });

        return undefined;

      }



      let cancelled = false;

      (async () => {

        await waitForLayoutPaint();

        if (cancelled) return;

        await replayWalkthrough({

          tourId: replayTourId,

          user,

          startTour,

          isMobile,

          openMobileNav,

        });

      })();



      return () => {

        cancelled = true;

      };

    }



    if (autoStartSessionRef.current || replayHandledRef.current) {

      return undefined;

    }



    if (!shouldAutoStartWalkthrough(user, persistedState)) {

      return undefined;

    }



    const targetPath = resolveAutoStartDashboardPath(user);

    if (location.pathname !== targetPath) {

      navigate(targetPath, { replace: true });

      return undefined;

    }



    const pendingTours = getToursForUser(user);

    if (pendingTours.length === 0) {

      return undefined;

    }



    autoStartSessionRef.current = true;

    let cancelled = false;



    (async () => {

      await waitForLayoutPaint();

      if (cancelled) return;



      const { steps, tourIds } = buildFirstRunSequence(user, persistedState);

      if (steps.length === 0) {

        const tourId = getDefaultAutoStartTourId(user);

        if (!tourId) return;



        const tour = getTourForUser(user, tourId);

        const firstStep = tour?.steps[0];

        if (firstStep) {

          await prepareStepTarget(firstStep, isMobile, { openMobileNav });

          await waitForLayoutPaint();

        }



        if (!cancelled) {

          startTour(tourId);

        }

        return;

      }



      const toursForSequence = pendingTours.filter((tour) => tourIds.includes(tour.id));

      const firstStep = steps[0];

      if (firstStep) {

        await prepareStepTarget(firstStep, isMobile, { openMobileNav });

        await waitForLayoutPaint();

      }



      if (!cancelled) {

        startSequence(toursForSequence);

      }

    })();



    return () => {

      cancelled = true;

    };

  }, [

    endActiveTour,

    isWalkthroughEnabled,

    isActive,

    isLoading,

    isMobile,

    location.pathname,

    navigate,

    openMobileNav,

    persistedState,

    startSequence,

    startTour,

    user,

  ]);



  return null;

}


